import React, { useState } from 'react';
import { cn } from '../../utils/cn';
import Icon from '../AppIcon';
import Button from './Button';
import { useNotifications } from '../../contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';

const NotificationPanel = ({ isOpen, onClose }) => {
  const { notifications, markAsRead, markAllAsRead, clearAll, removeNotification } = useNotifications();
  const [filter, setFilter] = useState('all'); // all, unread, delay, gate_change, status_update

  const filteredNotifications = notifications?.filter(n => {
    if (filter === 'unread') return !n?.read;
    if (filter === 'all') return true;
    return n?.type === filter;
  });

  const getIcon = (type) => {
    switch (type) {
      case 'delay':
        return { name: 'Clock', color: 'var(--color-warning)' };
      case 'gate_change':
        return { name: 'MapPin', color: 'var(--color-accent-blue)' };
      case 'status_update':
        return { name: 'Info', color: 'var(--color-accent-cyan)' };
      case 'cancellation':
        return { name: 'XCircle', color: 'var(--color-error)' };
      default:
        return { name: 'Bell', color: 'var(--color-accent-cyan)' };
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'delay':
        return 'Flight Delay';
      case 'gate_change':
        return 'Gate Change';
      case 'status_update':
        return 'Status Update';
      case 'cancellation':
        return 'Cancellation';
      default:
        return 'Notification';
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification?.read) {
      markAsRead(notification?.id);
    }
    if (notification?.action) {
      notification?.action();
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={cn(
          'fixed top-0 right-0 h-full w-full sm:w-[420px] bg-secondary border-l border-border z-[9999]',
          'transform transition-transform duration-300 ease-smooth',
          'flex flex-col shadow-2xl',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="Bell" size={20} color="var(--color-accent-cyan)" />
            <h2 className="text-lg font-heading font-semibold text-foreground">
              Notifications
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close notifications"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 p-4 border-b border-border overflow-x-auto">
          {[
            { value: 'all', label: 'All' },
            { value: 'unread', label: 'Unread' },
            { value: 'delay', label: 'Delays' },
            { value: 'gate_change', label: 'Gates' },
            { value: 'status_update', label: 'Updates' }
          ]?.map(tab => (
            <button
              key={tab?.value}
              onClick={() => setFilter(tab?.value)}
              className={cn(
                'px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap',
                filter === tab?.value
                  ? 'bg-accent-cyan text-accent-cyan-foreground'
                  : 'bg-tertiary text-muted-foreground hover:text-foreground'
              )}
            >
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Actions Bar */}
        {notifications?.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-tertiary/50">
            <Button
              variant="ghost"
              size="xs"
              onClick={markAllAsRead}
              className="text-xs"
            >
              Mark all read
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={clearAll}
              className="text-xs text-error hover:text-error"
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {filteredNotifications?.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <Icon name="Bell" size={48} color="var(--color-muted-foreground)" className="mb-4" />
              <p className="text-muted-foreground text-sm">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filteredNotifications?.map(notification => {
                const icon = getIcon(notification?.type);
                return (
                  <div
                    key={notification?.id}
                    className={cn(
                      'p-4 hover:bg-tertiary/50 transition-colors cursor-pointer',
                      !notification?.read && 'bg-accent-cyan/5'
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0 mt-1">
                        <Icon name={icon?.name} size={20} color={icon?.color} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <span className="text-xs font-medium text-accent-cyan">
                            {getTypeLabel(notification?.type)}
                          </span>
                          {!notification?.read && (
                            <span className="w-2 h-2 rounded-full bg-accent-cyan flex-shrink-0 mt-1" />
                          )}
                        </div>

                        <h3 className="text-sm font-medium text-foreground mb-1">
                          {notification?.title}
                        </h3>

                        {notification?.message && (
                          <p className="text-xs text-muted-foreground mb-2">
                            {notification?.message}
                          </p>
                        )}

                        {/* Metadata */}
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>
                            {formatDistanceToNow(new Date(notification?.timestamp), { addSuffix: true })}
                          </span>
                          {notification?.flightNumber && (
                            <span className="flex items-center gap-1">
                              <Icon name="Plane" size={12} />
                              {notification?.flightNumber}
                            </span>
                          )}
                        </div>

                        {/* Action Buttons */}
                        {notification?.actions && notification?.actions?.length > 0 && (
                          <div className="flex items-center gap-2 mt-3">
                            {notification?.actions?.map((action, idx) => (
                              <Button
                                key={idx}
                                variant={action?.variant || 'outline'}
                                size="xs"
                                onClick={(e) => {
                                  e?.stopPropagation();
                                  action?.onClick?.();
                                }}
                              >
                                {action?.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e?.stopPropagation();
                          removeNotification(notification?.id);
                        }}
                        className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Delete notification"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;